import React from 'react'
import { Button, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import RadioIcon from '@mui/icons-material/RadioRounded'
import type { MeStation } from '../api/me'

export interface StationSwitcherProps {
  stations: MeStation[]
  activeStationId?: number
  variant?: 'full' | 'icon'
  onSelect?: (station: MeStation) => void
}

const StationSwitcher: React.FC<StationSwitcherProps> = ({
  stations,
  activeStationId,
  variant = 'full',
  onSelect,
}) => {
  const activeStation = React.useMemo(() => {
    if (!stations?.length) {
      return undefined
    }
    return stations.find((station) => station.id === activeStationId) ?? stations[0]
  }, [stations, activeStationId])

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const hasStations = stations.length > 0
  const label = activeStation?.name ?? 'Select station'

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasStations) {
      return
    }
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (station: MeStation) => {
    onSelect?.(station)
    setAnchorEl(null)
  }

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title={label}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              color="inherit"
              aria-label="Select station"
              sx={{ width: 40, height: 40 }}
              onClick={handleOpen}
              disabled={!hasStations}
            >
              <RadioIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <span style={{ display: 'inline-flex' }}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<RadioIcon />}
            sx={{ borderRadius: 999, textTransform: 'none', px: 2, fontWeight: 500 }}
            onClick={handleOpen}
            disabled={!hasStations}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
          >
            {label}
          </Button>
        </span>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} keepMounted>
        {stations.map((station) => (
          <MenuItem
            key={station.id}
            selected={station.id === activeStation?.id}
            onClick={() => handleSelect(station)}
          >
            {station.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default StationSwitcher
